import React, { useRef, useState } from 'react';
import {
  Menu, MenuItem, ListItemIcon, Tooltip, CircularProgress,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PillNav from 'Components/reactbits/PillNav';
import { ContentLayoutDetails } from 'Pages/private/content-layout-details';
import { useBulkDownloadAll, DOWNLOAD_MODES } from 'hooks/useBulkDownloadAll';
import { V2ThemeProvider } from '../_shared/V2Layout';
import { SharedTab } from './SharedTab';
import styles from './index.module.css';

// Your Videos (modern) reuses the redesigned content-layout detail page (left
// sidebar + grid + cards), driven by a floating PillNav instead of the route
// param / back button. Videos → categories (groupType 1), Photo albums →
// albums (3). Shared reuses the existing contribution-links feature from the
// classic /your-videos "Shared" tab (see SharedTab.jsx).
const PILLS = ['Videos', 'Photo albums', 'Shared'];
const PILL_GROUP_TYPE = ['1', '3', 'shared'];

export const YourVideosV2 = () => {
  const [pillIndex, setPillIndex] = useState(0);
  const groupType = PILL_GROUP_TYPE[pillIndex];
  const downloadControlRef = useRef(null);

  // "Download all" — same control (and same hook) as the Dashboard Storage
  // card's download icon; this is where it used to live on the old /your-videos.
  const {
    downloadAllContent, preparingDownload, bulkDownloadProgress,
    downloadMenuAnchor, setDownloadMenuAnchor,
  } = useBulkDownloadAll();

  // The mode switcher now lives inside the content header (left of Search)
  // instead of a separate pill bar at the top next to the heading.
  const modeSwitcher = (
    <PillNav items={PILLS} activeIndex={pillIndex} onChange={setPillIndex} accent="#15171c" />
  );

  return (
    <V2ThemeProvider withBaseline={false}>
      <div className={styles.subHeader}>
        <div className={styles.titleBlock}>
          <h1 className={styles.pageTitle}>Your Content</h1>
          <p className={styles.pageSub}>Your videos, photo albums, and shared content.</p>
        </div>

        {groupType !== 'shared' && (
          <div ref={downloadControlRef} className={styles.downloadControl}>
            <span
              role="button"
              tabIndex={0}
              className={styles.downloadText}
              onClick={!preparingDownload ? () => setDownloadMenuAnchor(downloadControlRef.current) : undefined}
              onKeyDown={(e) => {
                if (!preparingDownload && e.key === 'Enter') setDownloadMenuAnchor(downloadControlRef.current);
              }}
              style={{ cursor: preparingDownload ? 'default' : 'pointer' }}
            >
              Download all your videos & photos
            </span>
            <Tooltip title={!preparingDownload
              ? 'Download all content'
              : bulkDownloadProgress.total > 0
                ? `Downloading ${bulkDownloadProgress.current}/${bulkDownloadProgress.total}`
                : 'Downloading…'}
            >
              <span>
                <button
                  type="button"
                  className={styles.downloadIconButton}
                  onClick={() => setDownloadMenuAnchor(downloadControlRef.current)}
                  disabled={preparingDownload}
                >
                  {preparingDownload
                    ? <CircularProgress size={18} style={{ color: '#5aa5ef' }} />
                    : <DownloadRoundedIcon fontSize="small" />}
                </button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1, borderRadius: 2, minWidth: 220 } }}
            >
              {DOWNLOAD_MODES.map(({ mode, label, Icon }) => (
                <MenuItem
                  key={mode}
                  onClick={() => {
                    setDownloadMenuAnchor(null);
                    downloadAllContent(mode);
                  }}
                  sx={{ fontSize: 14, fontWeight: 600, py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 30 }}><Icon size={17} /></ListItemIcon>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}
      </div>

      {groupType === 'shared' ? (
        <SharedTab headerLeading={modeSwitcher} />
      ) : (
        <ContentLayoutDetails
          key={groupType}
          groupTypeProp={groupType}
          hideBack
          showHeader
          topOffset={152}
          headerLeading={modeSwitcher}
          disableReorder
          sidebarEditable
        />
      )}
    </V2ThemeProvider>
  );
};

export default YourVideosV2;
